import { useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Download, Inbox, X, AlertCircle } from 'lucide-react';

import { exportCsv, csvFilename } from '@/lib/csv';
import { entityTypeToPath } from '@/features/audit/entity-link';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

import { EmptyState, ErrorState, Skeleton } from '@/components/chassis/States';
import { Pager } from '@/components/chassis/Pager';
import { useAuditLog } from '@/features/audit/useAuditLog';
import { AUDIT_ACTION_GROUPS } from '@/features/audit/audit-actions';
import { groupOf, kindOf, type EventKind, type FeedGroup } from '@/features/audit/f5-kinds';
import { humanizeAction, humanizeEntity } from '@/features/audit/humanize';
import type { AuditLogEntry, AuditLogFilters } from '@/types/api';
import { cn } from '@/lib/utils';
import { useT, useLang, type StringKey } from '@/i18n';
import { AuditDetailModal } from './components/AuditDetailModal';

// ---------------------------------------------------------------------------
// F5-T6b · Admin 09 — Auditoría (contrato 2026-08-28). Feed inmutable con
// entidad navegable por evento, 5 filtros (no veinte), 4 tipos de evento con
// punto de color + chip textual, y el modal del antes y el después.
//
// MUERE con el contrato (dies): el buscador libre sobre el feed (el input
// userQuery que buscaba actores por texto — «buscar "Marisol" en auditoría es
// pasear por accesos a pacientes sin justificación»). El export del feed, los
// filtros de acción exacta y de fechas se CONSERVAN (E-23: vivos, ni
// dibujados ni matados).
//
// NECESITA-SERVER (dibujado, sin endpoint hoy — NO se finge):
//   · Contadores por filtro: /admin/audit filtra por action exacta; el grupo
//     se filtra client-side sobre la página cargada → un contador global por
//     grupo mentiría. Las píldoras van sin contador.
//   · Filtro de grupo server-side (hoy solo aplica a la página de 50).
//   · Rol del actor AL MOMENTO del evento y sesión/dispositivo: el entry trae
//     userName/userEmail/ipAddress; el snapshot de rol no se guarda.
//   · Meta-auditoría de abrir el detalle: el detalle se arma client-side de
//     la fila ya cargada; no hay endpoint que registre esa lectura.
//   · «ya no existe» para entidad borrada: el entry no dice si la entidad
//     sigue viva — el link se dibuja solo para tipos con ruta (F-10).
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;

type QuickRange = 'today' | 'yesterday' | 'last7' | 'last30' | 'custom';

const GROUPS: FeedGroup[] = ['paciente', 'config', 'usuarios', 'exports'];
const GROUP_LABEL: Record<FeedGroup, StringKey> = {
  paciente: 'f5t6b.au.f.paciente',
  config: 'f5t6b.au.f.config',
  usuarios: 'f5t6b.au.f.usuarios',
  exports: 'f5t6b.au.f.exports',
};

const KIND_DOT: Record<EventKind, string> = {
  lectura: 'bg-nav-teal',
  cambio: 'bg-nav-navy',
  sensible: 'bg-nav-amber',
  irreversible: 'bg-nav-red',
};

const KIND_LABEL: Record<EventKind, StringKey> = {
  lectura: 'f5t6b.au.k.lectura',
  cambio: 'f5t6b.au.k.cambio',
  sensible: 'f5t6b.au.k.sensible',
  irreversible: 'f5t6b.au.k.irreversible',
};

function computeRange(q: QuickRange): { from?: string; to?: string } {
  if (q === 'custom') return {};
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

  const fmt = (d: Date): string => {
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  if (q === 'today') return { from: fmt(startOfToday), to: fmt(endOfToday) };
  if (q === 'yesterday') {
    const ys = new Date(startOfToday); ys.setDate(ys.getDate() - 1);
    const ye = new Date(endOfToday); ye.setDate(ye.getDate() - 1);
    return { from: fmt(ys), to: fmt(ye) };
  }
  if (q === 'last7') {
    const f = new Date(startOfToday); f.setDate(f.getDate() - 6);
    return { from: fmt(f), to: fmt(endOfToday) };
  }
  if (q === 'last30') {
    const f = new Date(startOfToday); f.setDate(f.getDate() - 29);
    return { from: fmt(f), to: fmt(endOfToday) };
  }
  return {};
}

/** «Hoy 09:42» · «Ayer 17:04» · «26 ago 09:14» — la fecha vive en cada fila. */
function fmtWhen(iso: string, locale: string, t: ReturnType<typeof useT>): string {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (dayStart === startOfToday) return t('f5t6b.au.today', { t: time });
  if (dayStart === startOfToday - 24 * 3600 * 1000) return t('f5t6b.au.yesterday', { t: time });
  return `${d.toLocaleDateString(locale, { day: '2-digit', month: 'short' })} ${time}`;
}

export function AuditLogPage(): JSX.Element {
  const t = useT();
  const lang = useLang();
  const locale = lang === 'es' ? 'es-AR' : 'en-US';
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const offset = (page - 1) * PAGE_SIZE;

  const filters: AuditLogFilters = useMemo(
    () => ({
      action: searchParams.get('action') ?? undefined,
      from: searchParams.get('from') ?? undefined,
      to: searchParams.get('to') ?? undefined,
    }),
    [searchParams],
  );
  const groupFilter = (searchParams.get('g') as FeedGroup | null) ?? null;
  const quickRange: QuickRange = (searchParams.get('quick') as QuickRange | null) ?? 'custom';

  const [draft, setDraft] = useState<AuditLogFilters>(filters);
  const [selected, setSelected] = useState<AuditLogEntry | null>(null);

  const { data, isLoading, error, refetch } = useAuditLog(filters, PAGE_SIZE, offset);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const applyFilters = (
    overrides: Partial<AuditLogFilters & { g?: FeedGroup | null; quick?: QuickRange }> = {},
  ): void => {
    const next = new URLSearchParams();
    const finalAction = overrides.action !== undefined ? overrides.action : draft.action;
    const finalFrom = overrides.from !== undefined ? overrides.from : draft.from;
    const finalTo = overrides.to !== undefined ? overrides.to : draft.to;
    const finalGroup = overrides.g !== undefined ? overrides.g : groupFilter;
    const finalQuick = overrides.quick !== undefined ? overrides.quick : quickRange;
    if (finalAction) next.set('action', finalAction);
    if (finalFrom) next.set('from', finalFrom);
    if (finalTo) next.set('to', finalTo);
    if (finalGroup) next.set('g', finalGroup);
    if (finalQuick && finalQuick !== 'custom') next.set('quick', finalQuick);
    next.set('page', '1');
    setSearchParams(next);
  };

  const clearFilters = (): void => {
    setDraft({});
    setSearchParams({});
  };

  const goToPage = (p: number): void => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(Math.min(Math.max(1, p), totalPages)));
    setSearchParams(next);
  };

  const setQuickRange = (q: QuickRange): void => {
    if (q === 'custom') {
      applyFilters({ quick: 'custom', from: '', to: '' });
      return;
    }
    const r = computeRange(q);
    setDraft((d) => ({ ...d, from: r.from, to: r.to }));
    applyFilters({ quick: q, from: r.from, to: r.to });
  };

  const hasActiveFilters = !!(filters.action || filters.from || filters.to || groupFilter);

  // CONSERVA (E-23): el export del feed sigue tal cual era — CSV client-side
  // de la página cargada, mismas columnas, mismo gate admin. Solo el vestido.
  const handleExport = (): void => {
    if (!data) return;
    exportCsv<AuditLogEntry>({
      filename: csvFilename('audit-log'),
      columns: [
        { header: 'Timestamp', accessor: (e) => e.occurredAt },
        { header: 'User name', accessor: (e) => e.userName },
        { header: 'User email', accessor: (e) => e.userEmail },
        { header: 'User ID', accessor: (e) => e.userId },
        { header: 'Action', accessor: (e) => e.action },
        { header: 'Entity type', accessor: (e) => e.entityType },
        { header: 'Entity ID', accessor: (e) => e.entityId },
        { header: 'IP address', accessor: (e) => e.ipAddress },
        { header: 'Metadata', accessor: (e) => (e.metadata ? JSON.stringify(e.metadata) : '') },
      ],
      rows: data.items,
    });
  };

  // El filtro de grupo aplica sobre la página cargada (NECESITA-SERVER un
  // filtro server-side por grupo; el copy del vacío lo dice honesto).
  const visibleItems = useMemo(() => {
    if (!data) return [] as AuditLogEntry[];
    if (!groupFilter) return data.items;
    return data.items.filter((e) => groupOf(e.action) === groupFilter);
  }, [data, groupFilter]);

  const QUICK_CHIPS: Array<{ key: QuickRange; tk: StringKey }> = [
    { key: 'today', tk: 'admin.audit.quickFilter.today' },
    { key: 'yesterday', tk: 'admin.audit.quickFilter.yesterday' },
    { key: 'last7', tk: 'admin.audit.quickFilter.last7' },
    { key: 'last30', tk: 'admin.audit.quickFilter.last30' },
    { key: 'custom', tk: 'admin.audit.quickFilter.custom' },
  ];

  return (
    <div className="font-body text-nav-navy max-w-[1100px]">
      <header className="mb-6">
        <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-slate m-0">
          {t('f5t6b.au.kicker')}
        </p>
        <h1 className="font-head text-[32px] font-extrabold tracking-[-0.025em] leading-tight mt-1 mb-2">
          {t('f5t6b.au.title')}
        </h1>
        <p className="text-[15px] leading-relaxed text-nav-navy/90 m-0 max-w-[680px]">{t('f5t6b.au.lead')}</p>
        <p className="mt-2 text-[13.5px] leading-relaxed text-nav-slate max-w-[680px] m-0">{t('f5t6b.au.sub')}</p>
      </header>

      <div className="bg-nav-soft border-[1.5px] border-nav-card rounded-[14px] px-7 pt-7 pb-8 animate-fade-up-slow motion-reduce:animate-none">
        {/* Barra de filtros + export. Cinco filtros, no veinte. */}
        <div className="flex flex-wrap items-center gap-3 mb-[22px]">
          <div className="flex flex-wrap items-center gap-2">
            <FilterPill
              label={t('f5t6b.au.f.todo')}
              active={!groupFilter}
              onClick={() => applyFilters({ g: null })}
            />
            {GROUPS.map((g) => (
              <FilterPill
                key={g}
                label={t(GROUP_LABEL[g])}
                active={groupFilter === g}
                onClick={() => applyFilters({ g: groupFilter === g ? null : g })}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={!data || data.items.length === 0}
            className={cn(
              'ml-auto inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[13px] font-bold text-nav-navy',
              'hover:bg-nav-tealTint hover:border-nav-teal hover:text-nav-tealDark transition-colors motion-reduce:transition-none',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            {t('f5t6b.au.export')}
          </button>
        </div>

        {/* CONSERVA (E-23): filtros finos vivos (acción exacta, rangos de
            fecha). El buscador libre por actor (userQuery) MURIÓ (dies #2). */}
        <details className="mb-[22px] rounded-xl border-[1.5px] border-nav-card bg-white px-5 py-3">
          <summary className="cursor-pointer select-none min-h-[38px] flex items-center font-head text-[12.5px] font-bold text-nav-slate">
            {t('f5t6b.au.advanced')}
          </summary>
          <div className="pt-3 space-y-3">
            <div className="flex flex-wrap gap-2">
              {QUICK_CHIPS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  aria-pressed={quickRange === c.key}
                  onClick={() => setQuickRange(c.key)}
                  className={cn(
                    'inline-flex items-center min-h-[44px] px-[15px] rounded-lg font-head text-[12.5px] font-bold border-[1.5px] transition-colors motion-reduce:transition-none',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                    quickRange === c.key
                      ? 'bg-nav-navy text-white border-nav-navy hover:bg-nav-navyHover'
                      : 'bg-white text-nav-navy border-nav-line hover:bg-nav-tealTint hover:border-nav-teal hover:text-nav-tealDark',
                  )}
                >
                  {t(c.tk)}
                </button>
              ))}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                applyFilters({ quick: 'custom' });
              }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
            >
              <div className="space-y-1.5">
                <Label htmlFor="filter-action">{t('admin.audit.filter.action')}</Label>
                <Select
                  id="filter-action"
                  value={draft.action ?? ''}
                  onChange={(e) => setDraft({ ...draft, action: e.target.value || undefined })}
                >
                  <option value="">{t('admin.audit.filter.actionAll')}</option>
                  {AUDIT_ACTION_GROUPS.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.actions.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="filter-from">{t('admin.audit.filter.from')}</Label>
                <Input
                  id="filter-from"
                  type="datetime-local"
                  value={draft.from ?? ''}
                  onChange={(e) => setDraft({ ...draft, from: e.target.value || undefined })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="filter-to">{t('admin.audit.filter.to')}</Label>
                <Input
                  id="filter-to"
                  type="datetime-local"
                  value={draft.to ?? ''}
                  onChange={(e) => setDraft({ ...draft, to: e.target.value || undefined })}
                />
              </div>
              <div className="flex items-end justify-end gap-2">
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[13px] font-bold text-nav-navy hover:bg-nav-softer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                    {t('admin.audit.filter.clear')}
                  </button>
                )}
                <button
                  type="submit"
                  className="inline-flex items-center min-h-[44px] px-5 rounded-lg bg-nav-navy border-[1.5px] border-nav-navy text-white font-head text-[13px] font-bold hover:bg-nav-navyHover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
                >
                  {t('admin.audit.filter.apply')}
                </button>
              </div>
            </form>
          </div>
        </details>

        {/* Lista de eventos. */}
        {error ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-xl">
            <ErrorState
              icon={<AlertCircle className="h-5 w-5" aria-hidden="true" />}
              title={t('admin.audit.error.title')}
              body={(error as Error).message}
              action={{ label: t('admin.audit.refresh'), onClick: () => void refetch() }}
            />
          </div>
        ) : isLoading ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-xl p-4 space-y-2.5" aria-busy="true">
            {Array.from({ length: 7 }, (_, i) => (
              <Skeleton key={i} className="h-[76px] w-full" />
            ))}
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-xl">
            {hasActiveFilters ? (
              <EmptyState
                icon={<Inbox className="h-5 w-5" aria-hidden="true" />}
                title={t('f5t6b.au.empty.filteredTitle')}
                body={t('f5t6b.au.empty.filteredBody')}
                action={{ label: t('f5t6b.au.empty.seeAll'), onClick: clearFilters }}
              />
            ) : (
              // Feed vacío del día uno: SIN botón (no hay nada que hacer).
              <EmptyState
                icon={<Inbox className="h-5 w-5" aria-hidden="true" />}
                title={t('f5t6b.au.empty.day1Title')}
                body={t('f5t6b.au.empty.day1Body')}
              />
            )}
          </div>
        ) : (
          <div className="bg-white border-[1.5px] border-nav-card rounded-xl overflow-hidden">
            <ul className="m-0 p-0 list-none">
              {visibleItems.map((e, i) => (
                <EventRow
                  key={e.id}
                  entry={e}
                  index={i}
                  locale={locale}
                  onDetail={() => setSelected(e)}
                />
              ))}
            </ul>
            <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-t border-nav-hair">
              <span className="text-[13px] text-nav-slate tabular-nums">
                {t('f5t6b.au.footer.page', {
                  n: visibleItems.length,
                  total: total.toLocaleString(locale),
                  p: page,
                })}
              </span>
              <span className="text-[13px] text-nav-slate">{t('f5t6b.au.footer.immutable')}</span>
            </div>
          </div>
        )}

        <Pager
          page={page - 1}
          pages={totalPages}
          onPage={(n) => goToPage(n + 1)}
          prevLabel={t('f5t6b.au.pager.prev')}
          nextLabel={t('f5t6b.au.pager.next')}
          pageLabel={t('f5t6b.au.pager.pageOf', { p: page, tp: totalPages })}
        />
      </div>

      <AuditDetailModal entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 min-h-[44px] px-[15px] rounded-lg font-head text-[12.5px] font-bold border-[1.5px] transition-colors motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
        active
          ? 'bg-nav-navy text-white border-nav-navy hover:bg-nav-navyHover'
          : 'bg-white text-nav-navy border-nav-line hover:bg-nav-tealTint hover:border-nav-teal hover:text-nav-tealDark',
      )}
    >
      {label}
      {/* NECESITA-SERVER: el contador por filtro del dibujo — sin conteo por
          grupo server-side, un número acá mentiría. */}
    </button>
  );
}

function EventRow({
  entry,
  index,
  locale,
  onDetail,
}: {
  entry: AuditLogEntry;
  index: number;
  locale: string;
  onDetail: () => void;
}): JSX.Element {
  const t = useT();
  const lang = useLang();
  const kind = kindOf(entry.action);
  const entityPath = entityTypeToPath(entry.entityType, entry.entityId);
  const humanEnt = humanizeEntity(entry.entityType, lang);

  return (
    <li
      className="flex flex-wrap items-center gap-4 min-h-[76px] px-6 py-4 border-t border-nav-hair first:border-t-0 bg-white animate-fade-up motion-reduce:animate-none"
      style={{ animationDelay: `${Math.min(index, 12) * 0.04}s` }}
    >
      {/* Punto de tipo — acompaña, nunca sustituye al chip. */}
      <span
        className="h-7 w-7 rounded-full bg-nav-softer border-[1.5px] border-nav-hair flex items-center justify-center shrink-0"
        aria-hidden="true"
      >
        <span className={cn('h-2.5 w-2.5 rounded-full', KIND_DOT[kind])} />
      </span>

      <div className="flex-1 min-w-[220px]">
        <p className="m-0 flex items-center gap-2 flex-wrap">
          <span className="font-head text-[13.5px] font-bold">{humanizeAction(entry.action, lang)}</span>
          <span className="rounded-full border border-nav-card bg-nav-hair px-2 py-[2px] font-head text-[9.5px] font-bold uppercase tracking-[0.06em] text-nav-slate">
            {t(KIND_LABEL[kind])}
          </span>
        </p>
        {entry.entityType && (
          <p className="m-0 mt-1 text-[13px] leading-relaxed text-nav-slate">
            {humanEnt}
            {entry.entityId &&
              (entityPath ? (
                // Enlace <a> REAL (Link), alcanzable con Tab (contrato a11y).
                <Link
                  to={entityPath}
                  className="ml-1 text-nav-teal font-semibold underline underline-offset-2 tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal rounded"
                >
                  #{entry.entityId}
                </Link>
              ) : (
                <span className="ml-1 text-nav-faint tabular-nums">#{entry.entityId}</span>
              ))}
          </p>
        )}
      </div>

      <div className="shrink-0 text-right min-w-[150px]">
        <p className="m-0 font-head text-[12.5px] font-bold tabular-nums">
          {fmtWhen(entry.occurredAt, locale, t)}
        </p>
        <p className="m-0 mt-[3px] text-[12px] text-nav-slate truncate">
          {entry.userName ?? t('f5t6b.au.system')}
        </p>
      </div>

      <button
        type="button"
        onClick={onDetail}
        className="shrink-0 inline-flex items-center min-h-[44px] px-[15px] rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[12.5px] font-bold text-nav-navy hover:bg-nav-tealTint hover:border-nav-teal hover:text-nav-tealDark transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
      >
        {t('f5t6b.au.detail')}
      </button>
    </li>
  );
}
